/**
 * Confirm Dialog Modal
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  IconButton,
  Box,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: "danger" | "warning" | "info";
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    padding: "8px",
    maxWidth: "480px",
    margin: "16px",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "24px 24px 16px",
  fontSize: "1.25rem",
  fontWeight: 600,
}));

const StyledDialogContent = styled(DialogContent)({
  padding: "0 24px 16px",
});

const StyledDialogActions = styled(DialogActions)({
  padding: "16px 24px 24px",
  gap: "12px",
});

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = "Bestätigen",
  cancelText = "Abbrechen",
  onConfirm,
  onCancel,
  type = "info",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const getIcon = () => {
    switch (type) {
      case "danger":
        return <ErrorIcon sx={{ color: "error.main", fontSize: "1.5rem" }} />;
      case "warning":
        return (
          <WarningIcon sx={{ color: "warning.main", fontSize: "1.5rem" }} />
        );
      default:
        return <InfoIcon sx={{ color: "info.main", fontSize: "1.5rem" }} />;
    }
  };

  const getConfirmButtonColor = () => {
    switch (type) {
      case "danger":
        return "error";
      case "warning":
        return "warning";
      default:
        return "primary";
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleCancel}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ position: "relative" }}>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>

        <StyledDialogTitle>
          {getIcon()}
          {title}
        </StyledDialogTitle>

        <StyledDialogContent>
          <DialogContentText
            sx={{
              fontSize: "1rem",
              color: "text.primary",
              lineHeight: 1.6,
            }}
          >
            {message}
          </DialogContentText>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button
            onClick={handleCancel}
            variant="outlined"
            size="large"
            sx={{ minWidth: "100px" }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={getConfirmButtonColor() as any}
            size="large"
            sx={{ minWidth: "100px" }}
          >
            {confirmText}
          </Button>
        </StyledDialogActions>
      </Box>
    </StyledDialog>
  );
};

export default ConfirmDialog;
